import { useTrackLink, useTrackView } from '@seek/cmsu-analytics';
import { useTranslations } from '@vocab/react';
import {
  Box,
  IconHelp,
  Text,
  TextField,
  TooltipRenderer,
} from 'braid-design-system';
import isMobile from 'is-mobile';
import { useEffect, useRef } from 'react';

import {
  REGISTER_ACCOUNT_ERROR,
  TOOLTIP_PRESSED,
} from 'src/constants/pageViewUniqueKeys';
import translations from 'src/views/shared/forms/RegistrationForm/.vocab';

interface FieldProps {
  label: string;
  value?: string;
  errorMessage: string;
  description: string;
  placeholder: string;
  tooltip: string;
  onChange: React.FormEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

const TRACKING_ID = 'tax-identification-number';

/**
 * A text input field that displays a Tax Identification number.
 */
export const TaxIdentificationNumberField = ({
  label,
  value,
  errorMessage,
  description,
  placeholder,
  tooltip,
  ...rest
}: FieldProps) => {
  const trackTooltipPressed = useTrackLink(TOOLTIP_PRESSED, {
    tooltipType: 'tin help',
  });
  const trackFieldError = useTrackView(REGISTER_ACCOUNT_ERROR, {
    alertShownType: 'error',
    alertShownMessage: errorMessage,
    fieldName: TRACKING_ID,
  });
  const { t } = useTranslations(translations);
  // Using a ref to track the tooltip timeout instead of state since this won't need to trigger a rerender.
  const tooltipTimeout = useRef<NodeJS.Timeout | undefined>();
  // Track if an error event is sent to prevent sending multiple events for the same error.
  const hasSentErrorEvent = useRef(false);

  /**
   * Tracking the tooltip when it actives. I've taken some of this logic from the Braid source code so we trigger the event at the right time.
   * @ref https://github.com/seek-oss/braid-design-system/blob/01536889a2a81abf4def1ec7bbef6f147efd3b83/packages/braid-design-system/src/lib/components/TooltipRenderer/TooltipRenderer.tsx
   */
  const onTooltipPressed = () => {
    // This aligns with the Braid tooltip logic.
    const TIMEOUT = isMobile() ? 0 : 250;
    tooltipTimeout.current = setTimeout(() => trackTooltipPressed?.(), TIMEOUT);
  };

  /**
   * Clear the tooltip timeout when the tooltip is released.
   */
  const onTooltipReleased = () => {
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }
  };

  // Track the error message when it changes.
  useEffect(() => {
    if (!errorMessage) {
      hasSentErrorEvent.current = false;
    }

    if (errorMessage && !hasSentErrorEvent.current) {
      // Stop more events being sent when typing continues
      hasSentErrorEvent.current = true;
      trackFieldError?.();
    }
  }, [errorMessage, trackFieldError]);

  return (
    <TextField
      {...rest}
      id="taxIdentificationNumber"
      label={label}
      secondaryLabel={t('optional')}
      description={description}
      placeholder={placeholder}
      data={{ 'tracking-id': TRACKING_ID }}
      value={value ?? ''}
      message={errorMessage}
      tone={errorMessage ? 'critical' : undefined}
      reserveMessageSpace
      tertiaryLabel={
        <TooltipRenderer
          id="tax-identification-number-tooltip"
          tooltip={<Text size="small">{tooltip}</Text>}
        >
          {({ triggerProps }) => (
            <Box
              aria-label={t('Help')}
              {...triggerProps}
              onMouseEnter={onTooltipPressed}
              onFocus={onTooltipPressed}
              onMouseLeave={onTooltipReleased}
              onBlur={onTooltipReleased}
              display="inline"
            >
              <IconHelp tone="formAccent" />
            </Box>
          )}
        </TooltipRenderer>
      }
    />
  );
};
